import { Carousel } from '@faststore/ui'
import { memo, useEffect, useRef, useState } from 'react'
import { PauseIcon } from 'src/components/icons/PauseIcon'
import { PlayIcon } from 'src/components/icons/PlayIcon'

import './HeroCarousel.scss'
import Slide from './Slide/Slide'
import type { HeroCarouselItemsProps, HeroCarouselProps } from './hero-carousel'
import { graphql, useStaticQuery } from 'gatsby'

interface HeroCarouselSlidesProps {
  data: HeroCarouselProps
}

const HeroCarouselSlides = ({ data }: HeroCarouselSlidesProps) => {
  const heroCarouselImages = useStaticQuery(
    graphql`
      query HeroCarouselImages {
        allFile {
          nodes {
            publicURL
            childImageSharp {
              gatsbyImageData(formats: [WEBP], placeholder: BLURRED)
            }
          }
        }
      }
    `
  )

  const carouselRef = useRef<HTMLElement | null>(null)
  const arrowRef = useRef<HTMLElement | null>(null)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const [isAutoPlay, setIsAutoPlay] = useState<boolean>(false)

  const infiniteMode = data?.carouselItems?.length > 1
  const controls = data?.carouselItems?.length > 1 ? 'complete' : undefined

  const clickHandler = () => {
    if (isAutoPlay) {
      clearInterval(timerRef.current as NodeJS.Timer)
    } else {
      timerRef.current = setInterval(() => {
        arrowRef?.current?.click()
      }, 5000)
    }

    setIsAutoPlay((prev) => !prev)
  }

  useEffect(() => {
    let nextArrow

    if (carouselRef.current) {
      nextArrow = carouselRef.current.querySelector(
        "button[data-arrow='right']"
      ) as HTMLElement
    }

    if (data.autoplay.isEnabled && nextArrow && typeof window !== 'undefined') {
      setIsAutoPlay(true)
      arrowRef.current = nextArrow
      timerRef.current = setInterval(() => {
        arrowRef?.current?.click()
      }, data.autoplay.duration ?? 4000)
    }

    return () => clearInterval(timerRef.current as NodeJS.Timer)
  }, [data.autoplay])

  if (!data?.carouselItems?.length) {
    return <></>
  }

  return (
    <section
      className="ms-0 me-0 w-full m-0 h-auto relative"
      style={{
        marginTop: `${data?.marginTop}`,
        marginBottom: `${data?.marginBottom}`,
      }}
      ref={carouselRef}
    >
      <Carousel
        infiniteMode={infiniteMode}
        controls={controls}
        transition={{
          duration: 400,
          property: 'transform',
        }}
      >
        {data?.carouselItems?.map(
          (slide: HeroCarouselItemsProps, index: number) => {
            return (
              <Slide
                key={index}
                content={slide.content}
                background={slide.background}
                index={index}
                heroCarouselImages={heroCarouselImages}
              />
            )
          }
        )}
      </Carousel>
      {data?.carouselItems?.length > 1 && (
        <div
          role="presentation"
          onClick={clickHandler}
          id="autoplay-button"
          className="absolute md:bottom-4 md:left-4 flex cursor-pointer right-4 top-4 md:top-auto md:right-auto"
        >
          {isAutoPlay ? (
            <PauseIcon width={16} height={16} color="#000" />
          ) : (
            <PlayIcon width={16} height={16} color="#000" />
          )}
        </div>
      )}
    </section>
  )
}

export default memo(HeroCarouselSlides)
